import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Search,
  Filter,
  Calendar,
  Tag,
  Star,
  TrendingUp,
  Clock,
} from "../utils/icons";
import { SearchService, SearchResult } from "../services/searchService";
import {
  EnhancedSearchService,
  EnhancedSearchResponse,
  SearchFacets,
  trackResultClick,
} from "../services/enhancedSearchService";
import SEO from "./SEO";

interface SearchResultsPageProps {
  onContactClick?: () => void;
}

function SearchResultsPage({ onContactClick }: SearchResultsPageProps) {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [results, setResults] = useState<EnhancedSearchResponse>({
    blog: [],
    courses: [],
    prompts: [],
    total: 0,
    query: "",
    searchTime: 0,
    facets: {
      categories: {},
      types: {},
      authors: {},
      dateRanges: {},
      tags: {},
    },
    suggestions: [],
    relatedSearches: [],
    totalPages: 0,
    currentPage: 1,
  });

  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<
    "all" | "blog" | "course" | "prompt"
  >("all");
  const [sortBy, setSortBy] = useState<"relevance" | "date" | "title">(
    "relevance",
  );
  const [showFacets, setShowFacets] = useState(false);

  useEffect(() => {
    if (query.trim()) {
      performSearch(query);
    }
  }, [query, sortBy]);

  const performSearch = async (searchQuery: string) => {
    setLoading(true);
    try {
      const searchResponse = await EnhancedSearchService.enhancedSearch(
        searchQuery,
        {
          limit: 20,
          types: ["blog", "course", "prompt"],
          sortBy,
        },
      );
      setResults(searchResponse);
    } catch (error) {
      console.error("Search error:", error);
      // Fallback to basic search
      const basicResponse = await SearchService.searchAll(searchQuery, {
        limit: 20,
        types: ["blog", "course", "prompt"],
      });
      setResults({
        ...basicResponse,
        facets: {
          categories: {},
          types: {},
          authors: {},
          dateRanges: {},
          tags: {},
        },
        suggestions: [],
        relatedSearches: [],
        totalPages: Math.ceil(basicResponse.total / 20),
        currentPage: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredResults = () => {
    if (activeFilter === "all") {
      return [...results.blog, ...results.courses, ...results.prompts];
    }

    switch (activeFilter) {
      case "blog":
        return results.blog;
      case "course":
        return results.courses;
      case "prompt":
        return results.prompts;
      default:
        return [];
    }
  };

  const handleResultClick = (result: SearchResult) => {
    trackResultClick(result.id, query);
  };

  const renderSearchResult = (result: SearchResult) => (
    <div
      key={result.id}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Link
            to={result.url}
            className="text-xl font-semibold text-gray-900 hover:text-[#F98B3D] transition-colors duration-200"
            dangerouslySetInnerHTML={{
              __html: SearchService.highlightSearchTerms(result.title, query),
            }}
            onClick={() => handleResultClick(result)}
          />
          {result.description && (
            <p
              className="text-gray-600 mt-2 line-clamp-3"
              dangerouslySetInnerHTML={{
                __html: SearchService.highlightSearchTerms(
                  result.description,
                  query,
                ),
              }}
            />
          )}
          <div className="flex items-center mt-4 space-x-4 text-sm text-gray-500">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              <Tag className="h-3 w-3 mr-1" />
              {result.type}
            </span>
            {result.score && (
              <span className="inline-flex items-center text-green-600 font-medium">
                <Star className="h-3 w-3 mr-1" />
                {Math.round(result.score)}% match
              </span>
            )}
            {result.published_at && (
              <span className="inline-flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {new Date(result.published_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFacets = () => {
    if (!showFacets) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Filter className="h-5 w-5 mr-2" />
          Filters
        </h3>

        {/* Type Filter */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Content Type
          </h4>
          <div className="space-y-2">
            {[
              { key: "all", label: "All", count: results.total },
              { key: "blog", label: "Blog Posts", count: results.blog.length },
              {
                key: "course",
                label: "Courses",
                count: results.courses.length,
              },
              {
                key: "prompt",
                label: "Prompts",
                count: results.prompts.length,
              },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key as any)}
                className={`flex items-center justify-between w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                  activeFilter === key
                    ? "bg-[#F98B3D] text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span>{label}</span>
                <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Sort Options */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Sort By</h4>
          <div className="space-y-2">
            {[
              { key: "relevance", label: "Relevance", icon: Star },
              { key: "date", label: "Date", icon: Calendar },
              { key: "title", label: "Title", icon: Search },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setSortBy(key as any)}
                className={`flex items-center w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                  sortBy === key
                    ? "bg-[#F98B3D] text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Search Stats */}
        <div className="border-t pt-4">
          <div className="text-xs text-gray-500 space-y-1">
            <div className="flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              Search time: {results.searchTime.toFixed(0)}ms
            </div>
            <div className="flex items-center">
              <TrendingUp className="h-3 w-3 mr-1" />
              {results.total} total results
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRelatedSearches = () => {
    if (!results.relatedSearches.length) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Related Searches
        </h3>
        <div className="flex flex-wrap gap-2">
          {results.relatedSearches.map((relatedQuery, index) => (
            <Link
              key={index}
              to={`/search?q=${encodeURIComponent(relatedQuery)}`}
              className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#F98B3D] hover:text-white transition-colors"
            >
              {relatedQuery}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  const filteredResults = getFilteredResults();

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`Search Results for "${query}" - One80Labs`}
        description={`Search results for "${query}" across blog posts, courses, and prompts.`}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Search Results
          </h1>
          <div className="flex items-center space-x-4">
            <Search className="h-5 w-5 text-gray-400" />
            <span className="text-gray-600">
              {loading
                ? "Searching..."
                : `${results.total} results found for "${query}"`}
            </span>
            {results.searchTime > 0 && (
              <span className="text-sm text-gray-500">
                ({results.searchTime.toFixed(0)}ms)
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar with Facets */}
          <div className="w-64 flex-shrink-0">
            <button
              onClick={() => setShowFacets(!showFacets)}
              className="w-full mb-4 flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span className="flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Filters & Sort
              </span>
              <span className="text-gray-500">{showFacets ? "−" : "+"}</span>
            </button>
            {renderFacets()}
            {renderRelatedSearches()}
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Quick Filters */}
            <div className="mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">
                  Quick Filter:
                </span>
                <div className="flex space-x-2">
                  {[
                    { key: "all", label: "All", count: results.total },
                    {
                      key: "blog",
                      label: "Blog Posts",
                      count: results.blog.length,
                    },
                    {
                      key: "course",
                      label: "Courses",
                      count: results.courses.length,
                    },
                    {
                      key: "prompt",
                      label: "Prompts",
                      count: results.prompts.length,
                    },
                  ].map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => setActiveFilter(key as any)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                        activeFilter === key
                          ? "bg-[#F98B3D] text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {label} ({count})
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results */}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F98B3D]"></div>
              </div>
            ) : filteredResults.length > 0 ? (
              <div className="space-y-6">
                {filteredResults.map(renderSearchResult)}
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No results found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try different keywords or check your spelling
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                >
                  Back to Home
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchResultsPage;
